import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  type CarLocationSuggestion,
  CarsAvailabilityActions,
  DropoffType,
} from "@b2bportal/core-cars";
import type { AppDispatch } from "@b2bportal/core-types";
import {
  AutocompleteCategory,
  GroundSelectionEnum,
  PlaceIdEnum,
} from "@b2bportal/ground-api";
import { fetchPackageCarsAvailability } from "../../../store";
import type { PackageCarsAvailabilityPageProps } from "../types";
import { useFormatPackagePickupDropoffDates } from "@/pages/Packages/hooks";
import { PackageSearchSelectors } from "@/store/packages/selectors";
import { fetchPackageSearchParams } from "@/store/packages/thunks/general";

export const usePackageCarsAvailabilityPageFetch = ({
  search,
}: PackageCarsAvailabilityPageProps) => {
  const dispatch = useDispatch<AppDispatch>();

  let requestedSearch = useSelector(PackageSearchSelectors.getSearchFormValues);

  const { pickupDate, dropoffDate } = useFormatPackagePickupDropoffDates(
    search.fromDate,
    search.untilDate
  );

  useEffect(() => {
    if (
      !requestedSearch ||
      !requestedSearch?.origin ||
      !requestedSearch?.destination
    ) {
      // We don't have requestedSearch, let's get them
      dispatch(fetchPackageSearchParams(search))
        .unwrap()
        .then((newSearch) => {
          requestedSearch = newSearch;
        });
    }

    if (requestedSearch?.origin && requestedSearch?.destination) {
      const pickupLocation: CarLocationSuggestion = {
        category: AutocompleteCategory.places,
        categoryLabel: "Places",
        label:
          requestedSearch.destination.groundSelection.groundSelection
            .searchTerm,
        id: {
          Id: PlaceIdEnum.Grounds,
          groundSelection: {
            ...requestedSearch.destination.groundSelection.groundSelection,
            GroundSelection: GroundSelectionEnum.Place,
          },
        },
        trackingPropertiesV2: {
          ...requestedSearch.destination.trackingPropertiesV2,
        },
      };

      // dropoff is the same as pickup
      const requestedSearchData = {
        pickupLocation,
        dropoffLocation: pickupLocation,
        driverAge: search.driverAge ?? 25,
        pickupTime: pickupDate,
        dropoffTime: dropoffDate,
        dropoffType: DropoffType.SameLocation,
        searchType: GroundSelectionEnum.Place,
      };
      // Set requested search first
      dispatch(CarsAvailabilityActions.setRequestedSearch(requestedSearchData));
      // Set loading state before fetching

      dispatch(
        CarsAvailabilityActions.setCarsAvailabilityFromPackagesLoading()
      );
      // Then fetch cars
      dispatch(fetchPackageCarsAvailability({ search }));
    }
  }, [dispatch, requestedSearch]);
};
